import { useState, useEffect, useCallback, useRef } from 'react';
import { financialStatementApi } from '../../services/financialStatementApi';
import type { FSView, FSViewLine, LineConfig } from '../../types/financialStatement';
import { ConfirmModal } from '../ui/ConfirmModal';
import {
    Plus, Trash2, Copy, Star, Save, X, ChevronUp, ChevronDown,
    Edit3, Eye,
} from 'lucide-react';

interface Props {
    company: string;
    canEdit: boolean;
    onViewsChanged: () => void;
}

interface EditableLine {
    id: string;
    sortOrder: number;
    rowType: 'Data' | 'Subtotal' | 'Header' | 'Spacer' | 'Difference';
    sourceLineNr: number | null;
    displayNameGerman: string;
    displayNameEnglish: string;
    indentLevel: number;
    isBold: boolean;
    formulaSortOrders: string; // comma-separated row indices (1-based)
}

const ROW_TYPES: EditableLine['rowType'][] = ['Data', 'Subtotal', 'Header', 'Spacer', 'Difference'];

let nextId = 1;
function genId() { return `tmp-${nextId++}`; }

function toEditable(vl: FSViewLine, _index: number): EditableLine {
    return {
        id: `vl-${vl.ViewLineId}`,
        sortOrder: vl.SortOrder,
        rowType: vl.RowType,
        sourceLineNr: vl.SourceLineNr,
        displayNameGerman: vl.DisplayNameGerman ?? '',
        displayNameEnglish: vl.DisplayNameEnglish ?? '',
        indentLevel: vl.IndentLevel,
        isBold: vl.IsBold === 1,
        formulaSortOrders: vl.FormulaSortOrders ?? '',
    };
}

function reindex(lines: EditableLine[]): EditableLine[] {
    return lines.map((l, i) => ({ ...l, sortOrder: (i + 1) * 10 }));
}

/** Get a short display label for a line (used in formula selector) */
function getLineLabel(line: EditableLine, index: number, lineConfigs: LineConfig[]): string {
    const num = `#${index + 1}`;
    if (line.rowType === 'Data' && line.sourceLineNr) {
        const cfg = lineConfigs.find(lc => lc.LineNr === line.sourceLineNr);
        return `${num} ${line.sourceLineNr} - ${cfg?.LineNameEnglish || cfg?.LineNameGerman || 'Data'}`;
    }
    if (line.rowType === 'Subtotal' || line.rowType === 'Header' || line.rowType === 'Difference') {
        const name = line.displayNameEnglish || line.displayNameGerman || line.rowType;
        return `${num} ${name}`;
    }
    if (line.rowType === 'Spacer') return `${num} (Spacer)`;
    return `${num} ${line.rowType}`;
}

export function ViewBuilderTab({ company, canEdit, onViewsChanged }: Props) {
    const [views, setViews] = useState<FSView[]>([]);
    const [selectedViewId, setSelectedViewId] = useState<number | null>(null);
    const [filterType, setFilterType] = useState<'IncomeStatement' | 'BalanceSheet'>('IncomeStatement');
    const [editing, setEditing] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [editName, setEditName] = useState('');
    const [editCompany, setEditCompany] = useState<string | null>(null);
    const [editDefault, setEditDefault] = useState(false);
    const [editLines, setEditLines] = useState<EditableLine[]>([]);
    const [lineConfigs, setLineConfigs] = useState<LineConfig[]>([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isNew, setIsNew] = useState(false);

    const loadViews = useCallback(async () => {
        try {
            const result = await financialStatementApi.getViews({ company });
            setViews(result.views);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load views');
        }
    }, [company]);

    useEffect(() => { loadViews(); }, [loadViews]);

    useEffect(() => {
        financialStatementApi.getLinesConfig()
            .then(r => setLineConfigs(r.lines))
            .catch(() => {});
    }, []);

    const filteredViews = views.filter(v => v.ViewType === filterType);

    const handleSelectView = useCallback(async (viewId: number) => {
        if (editing) return;
        setSelectedViewId(viewId);
        setError(null);
    }, [editing]);

    const handleEdit = useCallback(async () => {
        if (!selectedViewId) return;
        try {
            const result = await financialStatementApi.getViewLines(selectedViewId);
            setEditName(result.view.ViewName);
            setEditCompany(result.view.CompanyCode);
            setEditDefault(result.view.IsDefault === 1);
            // Convert DB SortOrders in FormulaSortOrders to row indices
            const rawLines = result.lines.map(toEditable);
            const sortOrderToIndex = new Map<number, number>();
            rawLines.forEach((l, i) => sortOrderToIndex.set(l.sortOrder, i + 1));
            const converted = rawLines.map(l => ({
                ...l,
                formulaSortOrders: l.formulaSortOrders
                    ? l.formulaSortOrders.split(',')
                        .map(s => sortOrderToIndex.get(parseInt(s.trim(), 10)))
                        .filter((v): v is number => v !== undefined)
                        .map(v => String(v * 10))
                        .join(',')
                    : '',
            }));
            setEditLines(converted);
            setEditing(true);
            setIsNew(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load view');
        }
    }, [selectedViewId]);

    const handleNew = useCallback(() => {
        setEditName('');
        setEditCompany(null);
        setEditDefault(false);
        setEditLines([]);
        setEditing(true);
        setIsNew(true);
        setSelectedViewId(null);
    }, []);

    const handleCancel = useCallback(() => {
        setEditing(false);
        setIsNew(false);
        setError(null);
    }, []);

    const handleSave = useCallback(async () => {
        if (!editName.trim()) {
            setError('View name is required');
            return;
        }
        if (editLines.length === 0) {
            setError('At least one line is required');
            return;
        }

        setSaving(true);
        setError(null);
        try {
            const indexed = reindex(editLines);
            const lines = indexed.map(l => ({
                sortOrder: l.sortOrder,
                rowType: l.rowType,
                sourceLineNr: l.rowType === 'Data' ? l.sourceLineNr : null,
                displayNameGerman: l.displayNameGerman || null,
                displayNameEnglish: l.displayNameEnglish || null,
                indentLevel: l.indentLevel,
                isBold: l.isBold,
                formulaSortOrders: (l.rowType === 'Subtotal' || l.rowType === 'Difference')
                    ? l.formulaSortOrders || null : null,
            }));

            if (isNew) {
                await financialStatementApi.createView({
                    viewName: editName.trim(),
                    viewType: filterType,
                    companyCode: editCompany,
                    isDefault: editDefault,
                    lines,
                });
            } else if (selectedViewId) {
                await financialStatementApi.updateView(selectedViewId, {
                    viewName: editName.trim(),
                    companyCode: editCompany,
                    isDefault: editDefault,
                    lines,
                });
            }

            setEditing(false);
            setIsNew(false);
            await loadViews();
            onViewsChanged();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save view');
        } finally {
            setSaving(false);
        }
    }, [editName, editCompany, editDefault, editLines, isNew, filterType, selectedViewId, loadViews, onViewsChanged]);

    const handleDelete = useCallback(async () => {
        if (!selectedViewId) return;
        setShowDeleteConfirm(false);
        try {
            await financialStatementApi.deleteView(selectedViewId);
            setSelectedViewId(null);
            setEditing(false);
            await loadViews();
            onViewsChanged();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete view');
        }
    }, [selectedViewId, loadViews, onViewsChanged]);

    const handleDuplicate = useCallback(async () => {
        if (!selectedViewId) return;
        try {
            const result = await financialStatementApi.duplicateView(selectedViewId);
            await loadViews();
            setSelectedViewId(result.viewId);
            onViewsChanged();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to duplicate view');
        }
    }, [selectedViewId, loadViews, onViewsChanged]);

    const handleSetDefault = useCallback(async () => {
        if (!selectedViewId) return;
        try {
            await financialStatementApi.setDefaultView(selectedViewId);
            await loadViews();
            onViewsChanged();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to set default');
        }
    }, [selectedViewId, loadViews, onViewsChanged]);

    // Line editing helpers
    const addLine = useCallback((rowType: EditableLine['rowType'] = 'Data') => {
        setEditLines(prev => [...prev, {
            id: genId(),
            sortOrder: 0,
            rowType,
            sourceLineNr: null,
            displayNameGerman: '',
            displayNameEnglish: '',
            indentLevel: 0,
            isBold: rowType === 'Subtotal' || rowType === 'Difference',
            formulaSortOrders: '',
        }]);
    }, []);

    const updateLine = useCallback((id: string, updates: Partial<EditableLine>) => {
        setEditLines(prev => prev.map(l => l.id === id ? { ...l, ...updates } : l));
    }, []);

    const removeLine = useCallback((id: string) => {
        setEditLines(prev => {
            const lineToRemove = prev.find(l => l.id === id);
            if (!lineToRemove) return prev;

            // Calculate the sortOrder of the line being removed (based on its current index)
            const removeIndex = prev.indexOf(lineToRemove);
            const removedSortOrder = (removeIndex + 1) * 10;

            // Remove the line
            const remaining = prev.filter(l => l.id !== id);

            // Build mapping from old sortOrders to new sortOrders after reindex
            const oldToNew = new Map<number, number>();
            let newIdx = 0;
            for (let i = 0; i < prev.length; i++) {
                if (prev[i].id === id) continue;
                const oldSO = (i + 1) * 10;
                const newSO = (newIdx + 1) * 10;
                oldToNew.set(oldSO, newSO);
                newIdx++;
            }

            // Update formulaSortOrders in all Subtotal/Difference lines:
            // 1. Remove references to the deleted line
            // 2. Remap remaining references to new sortOrders
            return remaining.map(l => {
                if ((l.rowType === 'Subtotal' || l.rowType === 'Difference') && l.formulaSortOrders) {
                    const refs = l.formulaSortOrders.split(',')
                        .map(s => parseInt(s.trim(), 10))
                        .filter(n => !isNaN(n))
                        .filter(so => so !== removedSortOrder) // remove deleted reference
                        .map(so => oldToNew.get(so))           // remap to new sortOrders
                        .filter((v): v is number => v !== undefined);
                    return { ...l, formulaSortOrders: refs.join(',') };
                }
                return l;
            });
        });
    }, []);

    const moveLine = useCallback((id: string, direction: -1 | 1) => {
        setEditLines(prev => {
            const idx = prev.findIndex(l => l.id === id);
            if (idx < 0) return prev;
            const newIdx = idx + direction;
            if (newIdx < 0 || newIdx >= prev.length) return prev;
            const arr = [...prev];
            [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];

            // Build mapping from old sortOrders to new sortOrders after swap
            const swappedSO_A = (idx + 1) * 10;
            const swappedSO_B = (newIdx + 1) * 10;
            const oldToNew = new Map<number, number>();
            for (let i = 0; i < prev.length; i++) {
                oldToNew.set((i + 1) * 10, (i + 1) * 10); // identity by default
            }
            oldToNew.set(swappedSO_A, swappedSO_B);
            oldToNew.set(swappedSO_B, swappedSO_A);

            // Remap formulaSortOrders
            return arr.map(l => {
                if ((l.rowType === 'Subtotal' || l.rowType === 'Difference') && l.formulaSortOrders) {
                    const refs = l.formulaSortOrders.split(',')
                        .map(s => parseInt(s.trim(), 10))
                        .filter(n => !isNaN(n))
                        .map(so => oldToNew.get(so) ?? so)
                        .sort((a, b) => a - b);
                    return { ...l, formulaSortOrders: refs.join(',') };
                }
                return l;
            });
        });
    }, []);

    // Available source lines filtered by view type
    const availableSourceLines = lineConfigs.filter(lc => {
        if (filterType === 'IncomeStatement') return lc.LineType === 'ProfitLoss';
        return lc.LineType === 'Assets' || lc.LineType === 'Liabilities';
    });

    const selectedView = views.find(v => v.ViewId === selectedViewId);

    return (
        <div className="space-y-4">
            {!canEdit && (
                <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-yellow-50 text-yellow-700 text-sm">
                    <Eye className="h-4 w-4" /> View-only mode - you cannot modify views
                </div>
            )}

            {error && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                    {error}
                </div>
            )}

            {/* View type filter */}
            <div className="flex items-center gap-2">
                <button
                    onClick={() => { setFilterType('IncomeStatement'); setSelectedViewId(null); setEditing(false); }}
                    className={`px-3 py-1.5 text-sm rounded-md ${filterType === 'IncomeStatement' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                    Income Statement
                </button>
                <button
                    onClick={() => { setFilterType('BalanceSheet'); setSelectedViewId(null); setEditing(false); }}
                    className={`px-3 py-1.5 text-sm rounded-md ${filterType === 'BalanceSheet' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                    Balance Sheet
                </button>
                {canEdit && !editing && (
                    <button
                        onClick={handleNew}
                        className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700"
                    >
                        <Plus className="h-4 w-4" /> New View
                    </button>
                )}
            </div>

            {/* View list */}
            {!editing && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {filteredViews.map(view => (
                        <div
                            key={view.ViewId}
                            onClick={() => handleSelectView(view.ViewId)}
                            className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                                selectedViewId === view.ViewId
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                            <div className="flex items-start justify-between">
                                <div>
                                    <h3 className="text-sm font-medium text-gray-900">{view.ViewName}</h3>
                                    <p className="text-xs text-gray-500 mt-1">
                                        {view.CompanyCode ? view.CompanyCode.toUpperCase() : 'All companies'}
                                    </p>
                                </div>
                                {view.IsDefault === 1 && (
                                    <Star className="h-4 w-4 text-yellow-500 fill-yellow-500 flex-shrink-0" />
                                )}
                            </div>
                        </div>
                    ))}
                    {filteredViews.length === 0 && (
                        <div className="col-span-full text-center py-8 text-gray-500 text-sm">
                            No views found for this type.
                        </div>
                    )}
                </div>
            )}

            {/* Action bar for selected view */}
            {!editing && selectedView && canEdit && (
                <div className="flex items-center gap-2 border-t pt-4">
                    <button onClick={handleEdit} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">
                        <Edit3 className="h-4 w-4" /> Edit
                    </button>
                    <button onClick={handleDuplicate} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200">
                        <Copy className="h-4 w-4" /> Duplicate
                    </button>
                    {selectedView.IsDefault !== 1 && (
                        <button onClick={handleSetDefault} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200">
                            <Star className="h-4 w-4" /> Set Default
                        </button>
                    )}
                    <button onClick={() => setShowDeleteConfirm(true)} className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-50 text-red-700 rounded-md hover:bg-red-100">
                        <Trash2 className="h-4 w-4" /> Delete
                    </button>
                </div>
            )}

            {/* Editor */}
            {editing && (
                <div className="border rounded-lg p-4 space-y-4 bg-gray-50">
                    <h3 className="text-lg font-semibold text-gray-900">
                        {isNew ? 'New View' : 'Edit View'}
                    </h3>

                    {/* Header fields */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">View Name</label>
                            <input
                                type="text"
                                value={editName}
                                onChange={e => setEditName(e.target.value)}
                                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                placeholder="e.g. Standard Income Statement"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">Company</label>
                            <select
                                value={editCompany ?? ''}
                                onChange={e => setEditCompany(e.target.value || null)}
                                className="w-full rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                            >
                                <option value="">All companies</option>
                                <option value="dch">DCH</option>
                                <option value="dbi">DBI</option>
                                <option value="bin">BIN</option>
                            </select>
                        </div>
                        <div className="flex items-end">
                            <label className="inline-flex items-center gap-2 cursor-pointer py-1.5">
                                <input
                                    type="checkbox"
                                    checked={editDefault}
                                    onChange={e => setEditDefault(e.target.checked)}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                                />
                                <span className="text-sm text-gray-600">Default view</span>
                            </label>
                        </div>
                    </div>

                    {/* Line list */}
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <h4 className="text-sm font-medium text-gray-700">Lines ({editLines.length})</h4>
                            <div className="flex gap-1">
                                {ROW_TYPES.map(rt => (
                                    <button
                                        key={rt}
                                        onClick={() => addLine(rt)}
                                        className="px-2 py-1 text-xs bg-white border rounded hover:bg-gray-50"
                                    >
                                        + {rt}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="max-h-[500px] overflow-y-auto space-y-1">
                            {editLines.map((line, idx) => (
                                <LineEditor
                                    key={line.id}
                                    line={line}
                                    index={idx}
                                    total={editLines.length}
                                    allLines={editLines}
                                    availableSourceLines={availableSourceLines}
                                    lineConfigs={lineConfigs}
                                    onUpdate={(updates) => updateLine(line.id, updates)}
                                    onRemove={() => removeLine(line.id)}
                                    onMove={(dir) => moveLine(line.id, dir)}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Save/Cancel */}
                    <div className="flex items-center gap-2 border-t pt-4">
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                        >
                            <Save className="h-4 w-4" /> {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button
                            onClick={handleCancel}
                            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                        >
                            <X className="h-4 w-4" /> Cancel
                        </button>
                    </div>
                </div>
            )}
            {showDeleteConfirm && (
                <ConfirmModal
                    title="Delete View"
                    message="Are you sure you want to delete this view?"
                    confirmLabel="Delete"
                    onConfirm={handleDelete}
                    onCancel={() => setShowDeleteConfirm(false)}
                />
            )}
        </div>
    );
}

// --- Formula Multi-Select Dropdown ---

interface FormulaSelectProps {
    value: string; // comma-separated SortOrders
    allLines: EditableLine[];
    currentIndex: number;
    lineConfigs: LineConfig[];
    onChange: (value: string) => void;
}

function FormulaSelect({ value, allLines, currentIndex, lineConfigs, onChange }: FormulaSelectProps) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    // Only preceding lines (rows before the current one) can be referenced
    const precedingLines = allLines.slice(0, currentIndex);
    const reindexed = reindex(precedingLines);

    // Filter out references to sort orders that no longer exist (orphaned references)
    const validSortOrders = new Set(reindexed.map(l => l.sortOrder));
    const selectedSortOrders = new Set(
        value ? value.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n) && validSortOrders.has(n)) : []
    );

    const toggleRow = (sortOrder: number) => {
        const next = new Set(selectedSortOrders);
        if (next.has(sortOrder)) {
            next.delete(sortOrder);
        } else {
            next.add(sortOrder);
        }
        // Keep sorted
        onChange(Array.from(next).sort((a, b) => a - b).join(','));
    };

    const selectedCount = selectedSortOrders.size;
    const summaryLabel = selectedCount === 0
        ? 'Select rows...'
        : `${selectedCount} row${selectedCount > 1 ? 's' : ''} selected`;

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="rounded border border-gray-300 px-2 py-1 text-xs bg-white hover:bg-gray-50 text-left w-56 truncate"
            >
                {summaryLabel}
            </button>
            {open && (
                <div className="absolute z-50 mt-1 w-80 max-h-60 overflow-y-auto bg-white border border-gray-300 rounded-md shadow-lg">
                    {reindexed.length === 0 ? (
                        <div className="px-3 py-2 text-xs text-gray-400">No preceding rows available</div>
                    ) : (
                        reindexed.map((rl, i) => {
                            const label = getLineLabel(allLines[i], i, lineConfigs);
                            const checked = selectedSortOrders.has(rl.sortOrder);
                            return (
                                <label
                                    key={rl.id}
                                    className={`flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-blue-50 ${checked ? 'bg-blue-50' : ''}`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => toggleRow(rl.sortOrder)}
                                        className="rounded border-gray-300 h-3.5 w-3.5 text-blue-600"
                                    />
                                    <span className="truncate">{label}</span>
                                </label>
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
}

// --- Line Editor Sub-Component ---

interface LineEditorProps {
    line: EditableLine;
    index: number;
    total: number;
    allLines: EditableLine[];
    availableSourceLines: LineConfig[];
    lineConfigs: LineConfig[];
    onUpdate: (updates: Partial<EditableLine>) => void;
    onRemove: () => void;
    onMove: (direction: -1 | 1) => void;
}

function LineEditor({ line, index, total, allLines, availableSourceLines, lineConfigs, onUpdate, onRemove, onMove }: LineEditorProps) {
    const rowNum = index + 1;

    return (
        <div className="flex items-start gap-2 p-2 bg-white rounded border text-sm">
            {/* Row number */}
            <span className="text-xs font-mono text-gray-400 w-6 pt-1.5 text-right flex-shrink-0">
                #{rowNum}
            </span>

            {/* Order controls */}
            <div className="flex flex-col gap-0.5 pt-1">
                <button onClick={() => onMove(-1)} disabled={index === 0} className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30">
                    <ChevronUp className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => onMove(1)} disabled={index === total - 1} className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30">
                    <ChevronDown className="h-3.5 w-3.5" />
                </button>
            </div>

            {/* Row type */}
            <select
                value={line.rowType}
                onChange={e => onUpdate({ rowType: e.target.value as EditableLine['rowType'] })}
                className="rounded border border-gray-300 px-2 py-1 text-xs w-24 flex-shrink-0"
            >
                {ROW_TYPES.map(rt => <option key={rt} value={rt}>{rt}</option>)}
            </select>

            {/* Dynamic fields based on row type */}
            <div className="flex-1 flex flex-wrap items-center gap-2">
                {line.rowType === 'Data' && (
                    <select
                        value={line.sourceLineNr ?? ''}
                        onChange={e => onUpdate({ sourceLineNr: e.target.value ? Number(e.target.value) : null })}
                        className="rounded border border-gray-300 px-2 py-1 text-xs flex-1 min-w-[200px]"
                    >
                        <option value="">Select line...</option>
                        {availableSourceLines.map(lc => (
                            <option key={lc.LineNr} value={lc.LineNr}>
                                {lc.LineNr} - {lc.LineNameEnglish || lc.LineNameGerman}
                            </option>
                        ))}
                    </select>
                )}

                {(line.rowType === 'Subtotal' || line.rowType === 'Header' || line.rowType === 'Difference') && (
                    <input
                        type="text"
                        value={line.displayNameEnglish}
                        onChange={e => onUpdate({ displayNameEnglish: e.target.value })}
                        placeholder="Name"
                        className="rounded border border-gray-300 px-2 py-1 text-xs flex-1 min-w-[200px]"
                    />
                )}

                {(line.rowType === 'Subtotal' || line.rowType === 'Difference') && (
                    <FormulaSelect
                        value={line.formulaSortOrders}
                        allLines={allLines}
                        currentIndex={index}
                        lineConfigs={lineConfigs}
                        onChange={(v) => onUpdate({ formulaSortOrders: v })}
                    />
                )}

                {line.rowType !== 'Spacer' && (
                    <>
                        <select
                            value={line.indentLevel}
                            onChange={e => onUpdate({ indentLevel: Number(e.target.value) })}
                            className="rounded border border-gray-300 px-2 py-1 text-xs w-16"
                            title="Indent level"
                        >
                            {[0, 1, 2, 3].map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                        <label className="inline-flex items-center gap-1 text-xs flex-shrink-0">
                            <input
                                type="checkbox"
                                checked={line.isBold}
                                onChange={e => onUpdate({ isBold: e.target.checked })}
                                className="rounded border-gray-300 h-3.5 w-3.5"
                            />
                            Bold
                        </label>
                    </>
                )}
            </div>

            {/* Delete */}
            <button onClick={onRemove} className="p-1 text-red-400 hover:text-red-600 flex-shrink-0" title="Remove line">
                <Trash2 className="h-3.5 w-3.5" />
            </button>
        </div>
    );
}
